/**
 * Zod schemas for the Optimisation and Offcuts features.
 *
 * These are the shared validation contracts for:
 *  - NestJS controller input validation (ZodValidationPipe)
 *  - Frontend TanStack Query hook types (M-9)
 *
 * Per ARCHITECTURE.md §3.6, ADR-012 (dual-strategy FFD + BFD),
 * REQUIREMENTS.md FR-OPT-001..006, and DESIGN.md §5.2 endpoints 41–46.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

export const OPT_RUN_STATUSES = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'] as const;
export const OFFCUT_STATUSES = ['AVAILABLE', 'CONSUMED', 'SCRAPPED'] as const;

// ---------------------------------------------------------------------------
// POST /projects/:id/optimise — request body
// ---------------------------------------------------------------------------

/**
 * Optional overrides the caller can supply when triggering an optimisation
 * run. If omitted, the effective value is resolved from the override chain:
 *   Line-Item override → Project override → Tenant default (FR-OPT-001).
 */
export const OptimiseRequestSchema = z.object({
  /** Override the tenant/project default stock-bar length for this run (mm). */
  stockBarOverrideMm: z
    .number()
    .int()
    .min(1000, 'Stock bar override must be at least 1000 mm')
    .max(12000, 'Stock bar override must not exceed 12000 mm')
    .optional(),
  remarks: z.string().max(500).optional(),
});

export type OptimiseRequest = z.infer<typeof OptimiseRequestSchema>;

// ---------------------------------------------------------------------------
// GET /optimisation/runs — list query
// ---------------------------------------------------------------------------

export const ListOptimisationRunsQuerySchema = z.object({
  cursor: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** Filter to runs belonging to a specific project. */
  projectId: z.coerce.number().int().positive().optional(),
});

export type ListOptimisationRunsQuery = z.infer<typeof ListOptimisationRunsQuerySchema>;

// ---------------------------------------------------------------------------
// Optimisation run DTOs
// ---------------------------------------------------------------------------

/**
 * Summary shape for list responses — no bar layouts or cuts (kept light).
 */
export const OptimisationRunSummaryDtoSchema = z.object({
  id: z.number().int(),
  projectId: z.number().int(),
  tenantId: z.number().int(),
  runStatus: z.enum(OPT_RUN_STATUSES),
  algorithmUsed: z.string().max(32).nullable(),
  algorithmCandidates: z.string().max(64),
  effectiveKerfMm: z.number().int(),
  effectiveThresholdMm: z.number().int(),
  effectiveStockBarMm: z.number().int(),
  totalBarsUsed: z.number().int().nullable(),
  totalCutsCount: z.number().int().nullable(),
  totalWasteMm: z.number().int().nullable(),
  totalOffcutsProducedMm: z.number().int().nullable(),
  triggeredBy: z.number().int(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  errorText: z.string().nullable(),
  createdDate: z.string(),
});

export type OptimisationRunSummaryDto = z.infer<typeof OptimisationRunSummaryDtoSchema>;

/** Individual cut within a bar layout. */
export const OptimisationCutDtoSchema = z.object({
  id: z.number().int(),
  barLayoutId: z.number().int(),
  cutIndex: z.number().int(),
  sourceLineItemId: z.number().int(),
  component: z.string().max(32),
  lengthMm: z.number().int(),
});

export type OptimisationCutDto = z.infer<typeof OptimisationCutDtoSchema>;

/** One stock bar consumed by a run, with its nested cuts. */
export const OptimisationBarLayoutDtoSchema = z.object({
  id: z.number().int(),
  runId: z.number().int(),
  barIndex: z.number().int(),
  stockBarLengthMm: z.number().int(),
  totalCutLengthMm: z.number().int(),
  wasteMm: z.number().int(),
  offcutReturnedMm: z.number().int(),
  cuts: z.array(OptimisationCutDtoSchema),
});

export type OptimisationBarLayoutDto = z.infer<typeof OptimisationBarLayoutDtoSchema>;

/**
 * Full run detail — used for GET /optimisation/runs/:id.
 * Includes bar layouts with nested cuts.
 */
export const OptimisationRunDtoSchema = OptimisationRunSummaryDtoSchema.extend({
  barLayouts: z.array(OptimisationBarLayoutDtoSchema),
});

export type OptimisationRunDto = z.infer<typeof OptimisationRunDtoSchema>;

/** Paginated list of run summaries. */
export const PaginatedOptimisationRunsDtoSchema = z.object({
  items: z.array(OptimisationRunSummaryDtoSchema),
  nextCursor: z.number().int().nullable(),
  hasMore: z.boolean(),
  limit: z.number().int(),
});

export type PaginatedOptimisationRunsDto = z.infer<typeof PaginatedOptimisationRunsDtoSchema>;

// ---------------------------------------------------------------------------
// GET /optimisation/runs/:id/compare/:otherId — comparison response
// ---------------------------------------------------------------------------

export const OptimisationCompareDtoSchema = z.object({
  runA: OptimisationRunSummaryDtoSchema,
  runB: OptimisationRunSummaryDtoSchema,
  /** Which run produced better results: A, B, or TIE (equal bars and waste). */
  winner: z.enum(['A', 'B', 'TIE']),
  /** Difference in total waste mm (runA.totalWasteMm - runB.totalWasteMm). Negative = B wastes less. */
  wasteDiffMm: z.number().int().nullable(),
  /** Difference in bars used (runA.totalBarsUsed - runB.totalBarsUsed). Negative = B uses fewer. */
  barsDiff: z.number().int().nullable(),
});

export type OptimisationCompareDto = z.infer<typeof OptimisationCompareDtoSchema>;

// ---------------------------------------------------------------------------
// POST /offcuts/:id/transition — request body
// ---------------------------------------------------------------------------

/**
 * Legal transitions: AVAILABLE → CONSUMED | SCRAPPED.
 * CONSUMED and SCRAPPED are terminal — no further transitions allowed.
 */
export const OffcutTransitionSchema = z.object({
  toStatus: z.enum(['CONSUMED', 'SCRAPPED'], {
    errorMap: () => ({ message: 'toStatus must be CONSUMED or SCRAPPED' }),
  }),
  /** Short classification label (e.g. "damaged", "used in job #123"). Max 64 chars. */
  reason: z.string().max(64).optional(),
  /** Free-text note for audit trail. Max 500 chars. */
  note: z.string().max(500).optional(),
});

export type OffcutTransition = z.infer<typeof OffcutTransitionSchema>;

// ---------------------------------------------------------------------------
// GET /offcuts — list query
// ---------------------------------------------------------------------------

export const ListOffcutsQuerySchema = z.object({
  cursor: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  inventoryStatus: z.enum(OFFCUT_STATUSES).optional(),
  seriesId: z.coerce.number().int().positive().optional(),
});

export type ListOffcutsQuery = z.infer<typeof ListOffcutsQuerySchema>;

// ---------------------------------------------------------------------------
// Offcut item DTOs
// ---------------------------------------------------------------------------

export const OffcutItemDtoSchema = z.object({
  id: z.number().int(),
  tenantId: z.number().int(),
  sourceRunId: z.number().int().nullable(),
  sourceBarLayoutId: z.number().int().nullable(),
  seriesId: z.number().int(),
  lengthMm: z.number().int(),
  inventoryStatus: z.enum(OFFCUT_STATUSES),
  storageLocation: z.string().max(64).nullable(),
  createdDate: z.string(),
  updatedDate: z.string(),
});

export type OffcutItemDto = z.infer<typeof OffcutItemDtoSchema>;

/** Paginated list of offcut inventory items. */
export const PaginatedOffcutsDtoSchema = z.object({
  items: z.array(OffcutItemDtoSchema),
  nextCursor: z.number().int().nullable(),
  hasMore: z.boolean(),
  limit: z.number().int(),
});

export type PaginatedOffcutsDto = z.infer<typeof PaginatedOffcutsDtoSchema>;

// ---------------------------------------------------------------------------
// Enqueue response (returned by POST /projects/:id/optimise)
// ---------------------------------------------------------------------------

export const EnqueueOptimisationResponseSchema = z.object({
  runId: z.number().int(),
  status: z.literal('QUEUED'),
});

export type EnqueueOptimisationResponse = z.infer<typeof EnqueueOptimisationResponseSchema>;
